import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';

const CATEGORIES = ['MEALS', 'LODGING', 'TRANSPORT', 'MILEAGE', 'OTHER'];
const empty = () => ({ employeeId: '', purpose: '', fromDate: '', toDate: '' });

export function TravelReportModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(empty);
  const [items, setItems] = useState([{ category: 'TRANSPORT', description: '', amount: '' }]);
  const [error, setError] = useState(null);

  const employees = useQuery({ queryKey: ['hr', 'employees'], queryFn: () => hrApi.listEmployees({ limit: 200 }).then((r) => r.items), enabled: open });

  useEffect(() => { if (open) { setForm(empty()); setItems([{ category: 'TRANSPORT', description: '', amount: '' }]); setError(null); } }, [open]);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setItem = (i, k) => (e) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, [k]: e.target.value } : it)));

  const save = useMutation({
    mutationFn: (body) => hrApi.createTravelReport(body),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['hr', 'travel-reports'] }); onClose(); },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  function submit(e) {
    e.preventDefault();
    setError(null);
    if (!form.employeeId || !form.purpose.trim()) { setError(t('tr.error.required')); return; }
    const cleanItems = items.filter((i) => i.amount !== '' && Number(i.amount) >= 0).map((i) => ({ category: i.category, description: i.description || null, amount: Number(i.amount) }));
    save.mutate({ employeeId: form.employeeId, purpose: form.purpose.trim(), fromDate: form.fromDate || null, toDate: form.toDate || null, items: cleanItems });
  }

  const total = items.reduce((s, i) => s + (Number(i.amount) || 0), 0);

  return (
    <Modal open={open} onClose={onClose} title={t('tr.modal.title')}
      footer={<><Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
        <Button variant="primary" onClick={submit} disabled={save.isPending}>{save.isPending ? `${t('common.save')}…` : t('common.save')}</Button></>}>
      <form onSubmit={submit} className="form-stack">
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('tr.field.employee')}</span>
            <select className="input" value={form.employeeId} onChange={set('employeeId')} required>
              <option value="">-</option>
              {(employees.data || []).map((e) => <option key={e.id} value={e.id}>{e.firstName} {e.lastName}</option>)}
            </select>
          </label>
          <label className="field"><span className="field-label">{t('tr.field.from')}</span>
            <input className="input" type="date" value={form.fromDate} onChange={set('fromDate')} />
          </label>
          <label className="field"><span className="field-label">{t('tr.field.to')}</span>
            <input className="input" type="date" value={form.toDate} onChange={set('toDate')} />
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('tr.field.purpose')}</span>
          <input className="input" value={form.purpose} onChange={set('purpose')} required />
        </label>

        <div className="field">
          <span className="field-label">{t('tr.field.items')}</span>
          {items.map((it, i) => (
            <div key={i} className="field-row" style={{ marginBottom: 'var(--space-2)' }}>
              <select className="input" style={{ maxWidth: 150 }} value={it.category} onChange={setItem(i, 'category')}>
                {CATEGORIES.map((c) => <option key={c} value={c}>{t(`tr.cat.${c}`)}</option>)}
              </select>
              <input className="input" placeholder={t('tr.field.description')} value={it.description} onChange={setItem(i, 'description')} />
              <input className="input num" style={{ maxWidth: 120 }} type="number" min="0" step="0.01" placeholder={t('tr.field.amount')} value={it.amount} onChange={setItem(i, 'amount')} />
            </div>
          ))}
          <Button type="button" size="sm" variant="ghost" onClick={() => setItems((a) => [...a, { category: 'OTHER', description: '', amount: '' }])}>{t('tr.field.addItem')}</Button>
          <span className="muted text-sm" style={{ marginLeft: 'var(--space-3)' }}>{t('tr.col.total')}: {total.toFixed(2)}</span>
        </div>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
